import { demoFunc } from './dees-harness-overflow-text.demo.js';
import {
  DeesElement,
  html,
  css,
  customElement,
  type TemplateResult,
  property,
  state,
  cssManager,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../00theme.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-harness-overflow-text': DeesHarnessOverflowText;
  }
}

@customElement('dees-harness-overflow-text')
export class DeesHarnessOverflowText extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Agent Chat'];

  @property({ type: Number })
  accessor fadeWidthPx: number = 40;

  @property({ type: Number })
  accessor introDelayMs: number = 2000;

  @state()
  accessor overflowing: boolean = false;

  @state()
  accessor travelPx: number = 0;

  private readonly createdAt = performance.now();
  private resizeObserver: ResizeObserver | null = null;
  private visibilityObserver: IntersectionObserver | null = null;
  private measureFrame: number | null = null;
  private introTimeout: ReturnType<typeof setTimeout> | null = null;
  private introAnimation: Animation | null = null;
  private introHandled = false;

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        display: block;
        min-width: 0;
        width: 100%;
        overflow: hidden;
      }

      .viewport {
        display: block;
        box-sizing: border-box;
        width: 100%;
        min-width: 0;
        overflow: hidden;
        white-space: nowrap;
      }

      .viewport.overflowing {
        -webkit-mask-image: linear-gradient(
          to right,
          black 0,
          black calc(100% - var(--harness-overflow-fade-width)),
          transparent 100%
        );
        mask-image: linear-gradient(
          to right,
          black 0,
          black calc(100% - var(--harness-overflow-fade-width)),
          transparent 100%
        );
      }

      .text {
        display: inline-block;
        min-width: max-content;
        transform: translateX(0);
      }

      .viewport.overflowing:hover .text {
        animation: harness-overflow-reveal var(--harness-overflow-duration) var(--dees-ease-standard);
      }

      @keyframes harness-overflow-reveal {
        0%, 10%, 100% { transform: translateX(0); }
        72%, 88% { transform: translateX(calc(0px - var(--harness-overflow-travel))); }
      }

      @media (prefers-reduced-motion: reduce) {
        .viewport.overflowing:hover .text {
          animation: none;
        }
      }
    `,
  ];

  public render(): TemplateResult {
    const durationSeconds = Math.min(7, Math.max(3.2, this.travelPx / 80 + 2.4));
    return html`
      <span
        class=${`viewport ${this.overflowing ? 'overflowing' : ''}`}
        style=${`
          --harness-overflow-fade-width: ${this.fadeWidthPx}px;
          --harness-overflow-travel: ${this.travelPx}px;
          --harness-overflow-duration: ${durationSeconds}s;
        `}
      >
        <span class="text"><slot @slotchange=${this.scheduleMeasure}></slot></span>
      </span>
    `;
  }

  public firstUpdated(changedProperties: Map<PropertyKey, unknown>): void {
    super.firstUpdated(changedProperties);
    if (!this.isConnected) return;
    this.connectResizeObserver();
    this.scheduleMeasure();
    this.scheduleIntro();
  }

  public updated(changedProperties: Map<PropertyKey, unknown>): void {
    super.updated(changedProperties);
    if (!this.isConnected) return;
    if (changedProperties.has('fadeWidthPx')) this.scheduleMeasure();
    if (changedProperties.has('introDelayMs') && !this.introHandled) this.scheduleIntro();
  }

  public async connectedCallback(): Promise<void> {
    const connected = super.connectedCallback();
    if (this.hasUpdated) {
      this.connectResizeObserver();
      this.scheduleMeasure();
      this.scheduleIntro();
    }
    await connected;
  }

  private connectResizeObserver(): void {
    if (!this.isConnected || this.resizeObserver) return;
    const viewport = this.shadowRoot?.querySelector('.viewport');
    const text = this.shadowRoot?.querySelector('.text');
    if (!viewport || !text) return;
    this.resizeObserver = new ResizeObserver(this.scheduleMeasure);
    this.resizeObserver.observe(viewport);
    this.resizeObserver.observe(text);
    this.visibilityObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.scheduleMeasure();
      requestAnimationFrame(() => this.tryRunIntro());
    });
    this.visibilityObserver.observe(this);
  }

  private readonly scheduleMeasure = (): void => {
    if (!this.isConnected || this.measureFrame !== null) return;
    this.measureFrame = requestAnimationFrame(() => {
      this.measureFrame = null;
      if (!this.isConnected) return;
      const viewport = this.shadowRoot?.querySelector<HTMLElement>('.viewport');
      const text = this.shadowRoot?.querySelector<HTMLElement>('.text');
      if (!viewport || !text) return;
      this.measureOverflow(viewport, text);
      if (
        !this.introHandled
        && performance.now() - this.createdAt >= this.introDelayMs
      ) {
        requestAnimationFrame(() => this.tryRunIntro());
      }
    });
  };

  private scheduleIntro(): void {
    if (!this.isConnected || this.introHandled) return;
    if (this.introTimeout) clearTimeout(this.introTimeout);
    const remainingMs = Math.max(0, this.introDelayMs - (performance.now() - this.createdAt));
    this.introTimeout = setTimeout(() => {
      this.introTimeout = null;
      this.scheduleMeasure();
      requestAnimationFrame(() => this.tryRunIntro());
    }, remainingMs);
  }

  private tryRunIntro(): void {
    if (!this.isConnected || this.introHandled) return;
    const viewport = this.shadowRoot?.querySelector<HTMLElement>('.viewport');
    const text = this.shadowRoot?.querySelector<HTMLElement>('.text');
    if (!viewport || !text || viewport.clientWidth <= 0) return;
    this.measureOverflow(viewport, text);
    this.introHandled = true;
    if (!this.overflowing || this.travelPx <= 0) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const duration = Math.min(7000, Math.max(3200, this.travelPx / 80 * 1000 + 2400));
    this.introAnimation = text.animate([
      { transform: 'translateX(0)', offset: 0 },
      { transform: 'translateX(0)', offset: 0.1 },
      { transform: `translateX(-${this.travelPx}px)`, offset: 0.72 },
      { transform: `translateX(-${this.travelPx}px)`, offset: 0.88 },
      { transform: 'translateX(0)', offset: 1 },
    ], {
      duration,
      easing: 'cubic-bezier(0.2, 0, 0, 1)',
    });
    this.introAnimation.onfinish = () => {
      this.introAnimation = null;
    };
    this.introAnimation.oncancel = () => {
      this.introAnimation = null;
    };
  }

  private measureOverflow(viewportArg: HTMLElement, textArg: HTMLElement): void {
    const overflowPx = Math.max(0, textArg.scrollWidth - viewportArg.clientWidth);
    this.overflowing = overflowPx > 0.5;
    this.travelPx = this.overflowing ? overflowPx + this.fadeWidthPx : 0;
  }

  public async disconnectedCallback(): Promise<void> {
    if (this.measureFrame !== null) cancelAnimationFrame(this.measureFrame);
    if (this.introTimeout) clearTimeout(this.introTimeout);
    this.measureFrame = null;
    this.introTimeout = null;
    this.introAnimation?.cancel();
    this.introAnimation = null;
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.visibilityObserver?.disconnect();
    this.visibilityObserver = null;
    await super.disconnectedCallback();
  }
}
